import { makeChapterContent, } from "../../main/src/utils/ContentUtils";
import { HistoryType } from "../../main/src/utils/Utils";
import { CACHE_LANG, CACHE_SUBSCRIPTION_BOOKS, makeDumpCacheName, } from "./CacheHolder";

import { getBookIdWithPrefix } from "../utils/content/content";
import {
  makeAggregateUrl,
  makeHistoryUrl,
  parseErrorDefault,
  parseResponseContentLanguages,
  parseResponseDefault
} from "./not-sorted.utils";
import { makeRequest } from "./baseApi";
import { convertAbbreviations, convertBooks } from "./content/books/utils";
import { dictionaryDefaultFilter } from "../utils/search/search.constants";
import { normalizeLanguageFolders } from "@/shared/api/content/languages/utils";
import { CONTENT_MEMORY_CACHE_TIME } from "@/shared/api/constants";

export const fetchBaseDataRequest = (lang) =>
  makeRequest({
    url: "/aggregate/anonymous/" + lang,
    storageCacheName: makeDumpCacheName(lang),
    parseResponse: (response) => {
      const outData = {};
      const allData = response.data;

      if (allData["dictionary-books"]) {
        let dictionaries = allData["dictionary-books"].results;
        if (dictionaries) {
          dictionaries = dictionaries.map((item) => ({
            key: item.book_id,
            label: `${item.title} (${item.code})`,
          }));
          dictionaries.unshift(dictionaryDefaultFilter);
          outData.dictionaries = dictionaries;
        }
      }

      if (allData["bible-books"]?.results) {
        outData.bibles = convertBooks(allData["bible-books"].results);
      }

      if (allData["subscription-books"]) {
        outData.subscriptions = allData["subscription-books"].map((item) =>
          getBookIdWithPrefix(item.book_id),
        );
      }

      if (allData["search-periods"]) {
        let presets = allData["search-periods"];
        if (allData["search-apl"]) {
          presets.apl = {
            books: [
              ...(allData["search-apl"].books?.books || []),
              ...(allData["search-apl"].periodicals?.books || []),
              ...(allData["search-apl"].both?.books || []),
            ],
          };
        }
        outData.presets = presets;
      }

      if (allData["languages"]) {
        outData.langs = parseResponseContentLanguages(allData["languages"], lang);
      }

      if (allData["folders"]) {
        let folders = normalizeLanguageFolders(allData["folders"], lang).outList;
        outData.folders = folders;
      }
      if (allData.geoip) {
        outData.geoIpData = allData.geoip;
      }

      return outData;
    },
  });

export const fetchBaseUserDataRequest = () =>
  makeRequest({
    url: "/aggregate/authenticated",
    parseResponse: (response) => {
      const outData = {};
      const allData = response.data;

      if (allData["subscription-time"]) {
        outData.timeData = allData["subscription-time"];
      }
      if (allData["settings-library"]?.languages) {
        outData.languages = allData["settings-library"].languages;
      }
      if (allData["settings-custom"]) {
        outData.settings = allData["settings-custom"];
      }
      if (allData["subscription-unread"]?.unread) {
        outData.subsUnreadCount = allData["subscription-unread"].unread;
      }
      if (allData["history-search"]) {
        outData.searchHistory = allData["history-search"].items;
      }
      if (allData["history-read"]) {
        outData.reading = {};
        outData.reading.items = normalizeHistoryData(allData["history-read"].items);
        outData.reading.deleted = normalizeHistoryData(allData["history-read"].deleted);
      }
      if (allData["history-audio"]) {
        outData.listen = {};
        outData.listen.items = normalizeHistoryData(allData["history-audio"].items);
        outData.listen.deleted = normalizeHistoryData(allData["history-audio"].deleted);
      }
      if (allData["history-download"]) {
        outData.library = {};
        outData.library.items = normalizeHistoryData(allData["history-download"].items);
        outData.library.deleted = normalizeHistoryData(allData["history-download"].deleted);
      }
      if (allData["history-library"]) {
        outData.ml = {};
        outData.ml.items = normalizeHistoryData(allData["history-library"].items);
        outData.ml.deleted = normalizeHistoryData(allData["history-library"].deleted);
      }

      return outData;
    },
  });

/**
 *
 * @param {Object} oUrls  object with keyname and url e.g { book1: "/books/11", book2: "/books/2" }
 * @returns {Object} with multiple data from urls
 */
export const getAggregateRequest = (oUrls) => {
  return makeRequest({
    url: makeAggregateUrl(oUrls),
    parseResponse: parseResponseDefault,
  });
};

export const getSubscriptionBooksRequest = () =>
  makeRequest({
    url: "/subscriptions/books/short",
    storageCacheName: CACHE_SUBSCRIPTION_BOOKS,
    parseResponse: (response) => response.data.map((item) => getBookIdWithPrefix(item.book_id)),
  });

export const getLanguagesRequest = (idLangBeFirst) => {
  return makeRequest({
    url: "/content/languages/",
    type: "get",
    storageCacheName: CACHE_LANG,
    parseResponse: (response) => parseResponseContentLanguages(response.data, idLangBeFirst),
  });
};

export const getChapterContentRequest = (chapterId, bookRealType, bookType, highlight) => {
  const [bookId, chapterNumber] = chapterId.split(".");
  let url = `/content/books/${bookId}/chapter/${chapterNumber}?trans=all`;
  if (highlight) {
    url += "&highlight=" + highlight;
  }
  return makeRequest({
    url,
    memoryCache: true,
    memoryCacheTime: CONTENT_MEMORY_CACHE_TIME,
    parseResponse: (response) => makeChapterContent(response.data, bookRealType, bookType),
  });
};

export const getChapterContentRequestLite = (paraId) => {
  const [bookId, chapterNumber] = paraId.split(".");
  let url = `/content/books/${bookId}/chapter/${chapterNumber}`;
  return makeRequest({
    url,
    memoryCache: true,
    memoryCacheTime: CONTENT_MEMORY_CACHE_TIME,
    parseResponse: parseResponseDefault,
  });
};

const normalizeHistoryData = (list) => {
  return list.map((item) => ({
    ...item,
    id: getBookIdWithPrefix(item.book),
  }));
};

export const getHistoryRequest = (historyType, withDelete, start, end) => {
  return makeRequest({
    url: makeHistoryUrl(historyType, withDelete, start, end),
    type: "get",
    parseResponse: (response) => normalizeHistoryData(response.data.items),
  });
};

export const getAllHistoryRequest = async (start) => {
  const requests = {
    reading: makeHistoryUrl(HistoryType.READING, true, start),
    listen: makeHistoryUrl(HistoryType.LISTEN, true, start),
    ml: makeHistoryUrl(HistoryType.ML_HISTORY, true, start),
    library: makeHistoryUrl(HistoryType.LIBRARY, false, start),
  };
  const results = await getAggregateRequest(requests);

  const allHistory = {};
  Object.keys(requests).forEach((type) => {
    allHistory[type] = {};
    if (results[type]?.data?.items) {
      allHistory[type].items = normalizeHistoryData(results[type]?.data?.items);  
    }

    if (results[type]?.data?.deleted) {
      allHistory[type].deleted = normalizeHistoryData(results[type]?.data?.deleted);  
    }
  });

  return allHistory;
};

/**
 *
 * @param {*} historyType
 * @param {Object} params { items: [],  date: Date, deleted: [] }
 * @returns
 */
export const putHistoryRequest = (historyType, params) => {
  return makeRequest({
    url: `/settings/${historyType}/sync`,
    type: "put",
    headers: {
      "Content-Type": "application/json-patch+json",
      accept: "text/plain",
    },
    data: params,
    parseResponse: (response) => response.data.date,
  });
};

export const deleteSearchHistory = () => {
  return makeRequest({
    url: "/settings/search_history/",
    type: "delete",
    parseResponse: (response) => response.data.date,
  });
};

export const fetchLibraryLanguagesRequest = () => {
  return makeRequest({
    url: "settings/library/settings",
    type: "get",
    parseResponse: (response) => response.data.languages,
  });
};

export const setLibraryLanguagesRequest = (languages) => {
  const request = {
    headers: {
      "Content-Type": "application/json",
    },
    url: "settings/library/settings",
    type: "put",
    data: {
      languages,
      lu: new Date().toISOString(),
    },
    parseResponse: (response) => {
      return response.status;
    },
  };
  return makeRequest(request);
};

export const getWaveformRequest = (url, bookId) => {
  return fetch(url)
    .then((response) => response.json())
    .then(({ data, chapters }) => {
      return {
        waveform: data,
        paragraphsAudioPositions: chapters.map((chapter) => {
          chapter.id = `${bookId}.${chapter.paraId}`;
          if (chapter.start === 4294967.5) { // server bug
            chapter.start = 0;
          }
          if (chapter.end === 4294967.5) { // server bug
            chapter.end = 0;
          }
          delete chapter.paraId;
          return chapter;
        }),
      };
    });
};

export const getGeoIpData = () => {
  return makeRequest({
    url: "geoip",
    type: "get",
    parseResponse: parseResponseDefault,
  });
};

export const deleteMlItem = (id) => {
  return makeRequest({
    url: "settings/library",
    type: "delete",
    data: { id },
    parseResponse: (response) => response.status,
  });
};

export const addBooksToLibraryRequest = async (books) => {
  return await makeRequest({
    url: `/settings/${HistoryType.LIBRARY}`,
    type: "post",
    data: { books },
    parseResponse: (response) => response.status,
  });
};

export const deleteBooksFromLibraryRequest = async (books) => {
  const result = await makeRequest({
    url: `settings/${HistoryType.LIBRARY}`,
    type: "delete",
    data: { books },
    parseResponse: (response) => response.status,
  });
  await deleteMlItem(books);
  return result;
};

export const getBookAudioHistory = (bookId) => {
  return makeRequest({
    url: `settings/audio/${bookId}`,
    parseResponse: parseResponseDefault,
  });
};

export const getBookReadHistory = (bookId) => {
  return makeRequest({
    url: `settings/read/${bookId}`,
    parseResponse: parseResponseDefault,
  });
};

export const sendCFTypo = (data) => {
  return makeRequest({
    url: "cf/typo",
    type: "post",
    data,
    headers: {
      "Content-type": "multipart/form-data",
    },
    parseError: parseErrorDefault,
  });
};

export const getContactUsRequest = () =>
  makeRequest({
    url: "cf",
  });

export const sendContact = (data) =>
  makeRequest({
    url: "cf",
    type: "post",
    data,
    "Content-type": "multipart/form-data",
  });

export const getSettingsRequest = () => {
  return makeRequest({
    url: "settings/custom",
    parseResponse: parseResponseDefault,
  });
};

export const saveSettingsRequest = (data) => {
  return makeRequest({
    url: "settings/custom",
    type: "PATCH",
    data,
  });
};

export const getAbbreviationsRequest = () => {
  return makeRequest({
    url: "/content/books/abbreviations",
    parseResponse: (response) => convertAbbreviations(response.data),
  });
};

